import { Box, Card, CardContent, IconButton, TextField } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export const MediaCardRoot = styled(Card, {
  shouldForwardProp: prop => prop !== 'cardHeight' && prop !== 'selected',
})<{ cardHeight: number; selected?: boolean }>(({ theme, cardHeight, selected }) => ({
  position: 'relative',
  height: cardHeight,
  ...(selected && { border: `2px solid ${theme.palette.primary.main}` }),
  '&:hover': { boxShadow: theme.customShadows.dropdown },
  '&:hover .asset-actions': { opacity: 1 },
}));

export const MediaCardContent = styled(CardContent)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  '&:last-child': { paddingBottom: theme.spacing(2) },
}));

export const MediaCardThumbnail = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 120,
  marginBottom: 5,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.grey[200] },
}));

export const MediaCardPreviewFrame = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
}));

export const MediaCardContactFrame = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '& > div': {
    padding: '8px !important',
    margin: '0 !important',
    border: 'none !important',
    borderRadius: '0 !important',
    minHeight: 'auto !important',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  '& h6': {
    fontSize: '0.875rem !important',
    fontWeight: '600 !important',
    marginBottom: '4px !important',
    lineHeight: '1.2 !important',
  },
  '& p': {
    fontSize: '0.75rem !important',
    margin: '1px 0 !important',
    lineHeight: '1.1 !important',
  },
  '& a': {
    fontSize: '0.75rem !important',
    lineHeight: '1.1 !important',
  },
});

export const MediaCardActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  opacity: 0,
  transition: 'opacity 0.2s',
  backgroundColor: alpha(theme.palette.common.white, 0.9),
  borderRadius: theme.shape.borderRadius,
  padding: '2px',
}));

export const MediaCardName = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'editable' && prop !== 'nameGap' && prop !== 'clickable' && prop !== 'untitled',
})<{ editable?: boolean; nameGap: number; clickable?: boolean; untitled?: boolean }>(
  ({ theme, editable, nameGap, clickable = true, untitled }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s',
    cursor: clickable ? 'pointer' : 'default',
    minHeight: 24,
    marginBottom: nameGap,
    '& h6': {
      width: '100%',
      textAlign: 'center',
      ...(untitled && { color: theme.palette.text.secondary, fontStyle: 'italic' }),
    },
    ...(editable && {
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        '& .edit-icon': { opacity: 1 },
      },
    }),
  }),
);

export const MediaCardEditIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '6px',
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0,
  transition: 'opacity 0.2s',
  minWidth: '18px',
  height: '18px',
  padding: '2px',
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '& .MuiSvgIcon-root': {
    fontSize: '14px',
    color: theme.palette.primary.main,
  },
}));

export const MediaCardInlineEdit = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  padding: '4px 6px',
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.customShadows.dropdown,
  gap: '6px',
  zIndex: 15,
  minHeight: '24px',
  maxHeight: '24px',
}));

export const MediaCardInlineInput = styled(TextField)({
  flex: 1,
  '& .MuiInputBase-root': {
    typography: 'body2',
    height: '16px',
  },
  '& .MuiInputBase-input': {
    padding: '0 2px',
  },
});

export const MediaCardInlineAction = styled(IconButton)({
  padding: '2px',
  minWidth: '16px',
  height: '16px',
});
